/**
 * Sidebar Component
 * Dark navigation sidebar for HC Dashboard
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  RobotOutlined,
  AuditOutlined,
  FileSearchOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

type MenuItem = Required<MenuProps>['items'][number];

const menuItems: MenuItem[] = [
  {
    key: '/',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },
  {
    key: '/agents',
    icon: <RobotOutlined />,
    label: 'Agents',
  },
  {
    key: '/audits',
    icon: <AuditOutlined />,
    label: 'Audits',
  },
  {
    key: '/observations',
    icon: <FileSearchOutlined />,
    label: 'Observations',
  },
  {
    key: '/policies',
    icon: <SafetyCertificateOutlined />,
    label: 'Policies',
  },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  // Determine selected key based on current path
  const getSelectedKey = (): string => {
    const path = location.pathname;
    if (path === '/') return '/';
    // Handle nested routes
    const baseRoute = '/' + path.split('/')[1];
    return baseRoute;
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        background: '#001529',
      }}
      width={220}
    >
      {/* Logo Section */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <div
          style={{
            color: '#fff',
            fontSize: collapsed ? 16 : 18,
            fontWeight: 700,
            letterSpacing: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {collapsed ? 'HC' : 'HC Dashboard'}
        </div>
      </div>

      {/* Navigation Menu */}
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          borderRight: 0,
          marginTop: 8,
        }}
      />

      {/* Version Badge at Bottom */}
      {!collapsed && (
        <div
          style={{
            position: 'absolute',
            bottom: 60,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.45)',
            fontSize: 12,
          }}
        >
          v1.0.0
        </div>
      )}
    </Sider>
  );
};

export default Sidebar;
